/**
 * iter_226 · Phase 22 — Public single detail + preview + buy
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import api, { BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { optimizeImage } from "@/lib/mediaUrls";

const fmtUsd = (n) => `$${Number(n || 0).toFixed(2)}`;

const SingleDetailPage = () => {
  const { listing_id } = useParams();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/singles/${listing_id}/public`);
        setS(r.data);
      } catch { setS({ error: true }); }
    })();
  }, [listing_id]);

  const buy = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/singles/${listing_id}/checkout`, { origin_url: window.location.href });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally { setBusy(false); }
  };

  if (!s) return <div className="text-xs text-gray-500 p-8">Loading…</div>;
  if (s.error) return <div className="text-xs text-rose-400 p-8" data-testid="single-not-found">Not available.</div>;

  return (
    <div className="max-w-3xl mx-auto p-4 space-y-5" data-testid="single-detail">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="aspect-square rounded-xl overflow-hidden bg-white/5">
          {s.cover_url && <img src={optimizeImage(s.cover_url, 'card')} alt="" className="w-full h-full object-cover" />}
        </div>
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">Single</p>
          <h1 className="text-2xl font-black text-white">{s.title}</h1>
          <p className="text-sm text-gray-300">{s.primary_artist_name}</p>
          {s.featured_artists?.length > 0 && (
            <p className="text-[11px] text-gray-500">
              feat. {s.featured_artists.map((f) => f.name || f.user_id).join(", ")}
            </p>
          )}
          <audio
            controls
            preload="none"
            src={`${BACKEND_URL}/api/singles/${listing_id}/preview`}
            className="w-full mt-2"
            data-testid="single-preview-audio"
          />
          <p className="text-2xl font-black text-emerald-300 mt-3">{fmtUsd(s.price_usd)}</p>
          <Button
            onClick={buy}
            disabled={busy}
            data-testid="single-buy-btn"
            className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-black font-black"
          >
            {busy ? "Redirecting…" : "Buy · Personal-use download"}
          </Button>
          <p className="text-[10px] text-gray-500 mt-2 leading-relaxed">
            {s.personal_use_licence}
          </p>
        </div>
      </div>
      {s.description && (
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <p className="text-[10px] uppercase text-gray-500 font-black">About</p>
          <p className="text-sm text-gray-200 whitespace-pre-wrap mt-1">{s.description}</p>
        </div>
      )}
    </div>
  );
};

export default SingleDetailPage;
